import React from 'react';
import type { CampaignGenerationJob } from '../types';
import { useGetCampaignGenerationJobDetails } from '../../../../../query/acg/phase-1';
import { Alert, Spin } from 'antd';
import FilenameWithDownloader from '../../../Phase2/FilenameWithDownloader';
import CampaignGenerationStatus from '../components/CampaignGenerationStatus';
import UserWithAvatar from '../../../../../components/ui/UserWithAvatar';
import moment from 'moment';
import { useForceIntervalUpdate } from '../../../../../hooks/useForceUpdate';
import { getFilenameFromPath } from 'services/acg/utils';
import CampaignGenerationJobArgumentsInfo from './CampaignGenerationJobArgumentsInfo';

type Props = {
  campaignGenerationJob: CampaignGenerationJob,
};

function RenderTimeElapsedAndETA({ jobDetails }) {
  useForceIntervalUpdate(1000);
  const eta = moment(jobDetails.startedAt).add(
    jobDetails.estimatedCompletionTime || 15 * 60,
    'seconds',
  );
  return (
    <div>
      Time Elapsed: {moment(jobDetails.startedAt).fromNow(true)} / ETA:{' '}
      {eta.isAfter(moment()) ? eta.countdown().toString() : 'a few seconds'}
    </div>
  );
}

export default function ViewJobDetailsModalContent(props: Props) {
  const { data, isLoading } = useGetCampaignGenerationJobDetails(
    props.campaignGenerationJob.id,
  );
  const { data: jobDetails = props.campaignGenerationJob, user } = data || {};
  return (
    <Spin spinning={isLoading}>
      <div className="w-full space-y-6">
        {jobDetails.status === 'failed' && (
          <Alert message={jobDetails.failureReason} showIcon type="error" />
        )}
        <div className="flex items-center w-full">
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Routine Name</b>
            <div>{jobDetails.routineName}</div>
          </div>
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Started By</b>
            <UserWithAvatar user={user} />
          </div>
        </div>

        <div className="flex items-center w-full">
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Started At</b>
            <div>{moment(jobDetails.startedAt).format('YYYY-MM-DD HH:mm')}</div>
          </div>
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Status</b>
            <div className="flex flex-col w-full">
              <div style={{ maxWidth: 200 }}>
                <CampaignGenerationStatus campaignGenerationJob={jobDetails} />
              </div>
              {jobDetails.status === 'processing' && (
                <RenderTimeElapsedAndETA jobDetails={jobDetails} />
              )}
            </div>
          </div>
        </div>

        {jobDetails.status === 'processed' && (
          <div className="space-y-2 flex flex-col">
            <b>Result</b>
            {jobDetails.result.gcsPaths.map((gcsPath, index) => (
              <FilenameWithDownloader
                filename={getFilenameFromPath(gcsPath)}
                gcsPath={gcsPath}
                key={index}
                phase="phase1"
              />
            ))}
          </div>
        )}
        <div className="space-y-2 flex flex-col">
          <b>Arguments Info</b>
          <CampaignGenerationJobArgumentsInfo
            campaignGenerationJob={jobDetails}
          />
        </div>
      </div>
    </Spin>
  );
}
